import {
    ActionIcon,
    Box,
    Code,
    CopyButton,
    Flex,
    Text,
    Tooltip,
    useMantineColorScheme,
} from "@mantine/core";
import { IconCopy, IconSettings } from "@tabler/icons-react";
import { Highlight, type Language, themes } from "prism-react-renderer";
import { memo, type ReactNode } from "react";
import styled from "styled-components";
import "../styles/markdown.scss";

const _Wrapper = styled.div`
	font-family: sans-serif;
	text-align: center;
`;

const Pre = styled.pre`
	text-align: left;
	padding: 0 10px 10px;
	overflow: scroll;
	border-radius: 0 0 6px 6px;

	& .token-line {
		font-size: 14px;
		line-height: 20px;
		font-family: monospace;
	}
`;

const Line = styled.div`
	display: table-row;
`;

const LineContent = styled.span`
	display: table-cell;
`;

interface MessageItemCodeProps {
    children?: ReactNode;
    className?: string;
    inline?: boolean;
}

export const MessageItemCode = memo(function MessageItemCode(props: MessageItemCodeProps) {
    const lang = (props.className?.replace("language-", "") || "tsx") as Language;
    const codeContent = (
        Array.isArray(props.children) ? props.children.join(" ") : String(props.children ?? "")
    ).trim();
    const { colorScheme } = useMantineColorScheme();

    const isInline = !props.className;
    if (isInline) {
        return <Code>{props.children}</Code>;
    }

    return (
        <Box>
            <Flex
                justify="flex-start"
                align="center"
                direction="row"
                style={{
                    height: 36,
                    paddingRight: 4,
                    borderRadius: "6px 6px 0 0",
                    backgroundColor:
                        colorScheme === "dark" ? "rgb(30, 30, 30)" : "rgb(246, 248, 250)",
                }}
            >
                <IconSettings opacity={0.5} size={20} width={38} />
                <Text style={{ flex: 1 }} fw={700}>
                    {lang?.toUpperCase() || "BASE"}
                </Text>
                <CopyButton value={codeContent}>
                    {({ copied, copy }) => (
                        <Tooltip
                            label={copied ? "Code copied to clipboard" : "Copy code to clipboard"}
                            position="left"
                        >
                            <ActionIcon onClick={copy}>
                                <IconCopy opacity={0.4} size={20} />
                            </ActionIcon>
                        </Tooltip>
                    )}
                </CopyButton>
            </Flex>
            <Highlight
                theme={colorScheme === "dark" ? themes.vsDark : themes.github}
                code={codeContent}
                language={lang}
            >
                {({ className, style, tokens, getLineProps, getTokenProps }) => (
                    <Pre className={className} style={style}>
                        {tokens.map((line, lineIndex) => (
                            <Line
                                key={`${line.map((t) => t.content).join("")}-${lineIndex}`}
                                {...getLineProps({ line })}
                            >
                                <LineContent>
                                    {line.map((token, index) => (
                                        <span
                                            key={`${token.content}-${index}`}
                                            {...getTokenProps({ token })}
                                        />
                                    ))}
                                </LineContent>
                            </Line>
                        ))}
                    </Pre>
                )}
            </Highlight>
        </Box>
    );
});
